/**
 * Cihazlar penceresi: kiracıdaki Octo.Agent kurulumları, çevrimiçi durumları, yazıcıları ve
 * sistem rolü için eşleştirme kodu üretimi. Kurulumun tamamı buradan başlar: kod üret →
 * istemcide ajanı çalıştır → kodu gir.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Download, KeyRound, Printer, RefreshCw } from 'lucide-react';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { Spinner } from '@/shared/ui/Spinner';
import { queryKeys } from '@/shared/api/queryKeys';
import { useAuthStore } from '@/entities/session/model/authStore';
import { cn } from '@/shared/lib/cn';
import { deviceApi, type DeviceDto, type DevicePrinterDto, type PairingCodeDto } from '../api/deviceApi';
import { useLocalDevice } from '../lib/localDevice';
import { ComPortsEditor } from './ComPortsEditor';
import { PrintJobsPanel } from './PrintJobsPanel';
import { PrinterEditDialog } from './PrinterEditDialog';

export function DevicesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isSys = useAuthStore((s) => !!s.user?.isSysRol);
  const local = useLocalDevice(open);
  const devices = useQuery({
    queryKey: queryKeys.device.list(),
    queryFn: () => deviceApi.list(),
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });
  const [pairing, setPairing] = useState<PairingCodeDto | null>(null);
  const [copied, setCopied] = useState(false);
  const createCode = useMutation({
    mutationFn: () => deviceApi.createPairingCode(),
    onSuccess: (code) => {
      setPairing(code);
      setCopied(false);
    },
  });

  /*
   * "Octo Agent indir": yönetici exe'yi buradan alıp istemciye götürür. Paket sunucudaki
   * klasörden (DeviceAgent:PackageDirectory) gelir; klasör yoksa dış adres (PackageUrl)
   * açılır; ikisi de yoksa düğme pasif ve sebebi başlıkta yazar.
   */
  const pkg = useQuery({
    queryKey: queryKeys.device.agentPackage(),
    queryFn: () => deviceApi.agentPackage(),
    enabled: open && isSys,
    staleTime: 60_000,
  });
  const downloadAgent = useMutation({
    mutationFn: async () => {
      const info = pkg.data;
      if (!info) return;
      if (!info.enabled && info.url) {
        window.open(info.url, '_blank', 'noopener');
        return;
      }
      const blob = await deviceApi.downloadAgentPackage();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = info.fileName ?? 'Octo.Agent.exe';
      a.click();
      URL.revokeObjectURL(a.href);
    },
  });
  const agentAvailable = !!pkg.data && (pkg.data.enabled || !!pkg.data.url);

  const copyCode = () => {
    if (!pairing) return;
    void navigator.clipboard?.writeText(pairing.code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t('shell.user.devicesTitle')}
      size="lg"
      footer={
        <>
          {isSys ? (
            <>
              <Button
                variant="secondary"
                onClick={() => downloadAgent.mutate()}
                loading={downloadAgent.isPending}
                disabled={!agentAvailable}
                title={agentAvailable ? undefined : t('shell.user.devicesAgentNoPackage')}
                data-testid="download-agent"
              >
                <Download className="h-4 w-4" />
                {t('shell.user.devicesDownloadAgent')}
                {pkg.data?.version ? <span className="text-fg-muted ml-1 text-xs">v{pkg.data.version}</span> : null}
              </Button>
              <Button onClick={() => createCode.mutate()} loading={createCode.isPending}>
                <KeyRound className="h-4 w-4" /> {t('shell.user.devicesPair')}
              </Button>
            </>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {t('common:Close')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Bu bilgisayar: ajan varsa kimliği, yoksa kurulum ipucu. */}
        <div className="border-border bg-bg-muted flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <span
            className={cn('h-2.5 w-2.5 shrink-0 rounded-full', local.data?.connected ? 'bg-success' : local.data ? 'bg-warning' : 'bg-fg-subtle')}
            aria-hidden
          />
          {local.data ? (
            <span className="min-w-0 truncate">
              {t('shell.user.devicesLocal')}: <strong>{local.data.deviceCode}</strong>
              {local.data.connected ? null : <span className="text-fg-muted"> · {t('shell.user.devicesLocalOffline')}</span>}
              {local.data.agentVersion ? <span className="text-fg-subtle"> · v{local.data.agentVersion}</span> : null}
            </span>
          ) : (
            <span className="text-fg-muted">{t('shell.user.devicesNoAgent')}</span>
          )}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => void qc.invalidateQueries({ queryKey: queryKeys.device.all })} title={t('shell.user.devicesRefresh')}>
            <RefreshCw className={cn('h-4 w-4', devices.isFetching && 'animate-spin')} />
          </Button>
        </div>

        {pairing ? (
          <div className="border-brand-500/40 bg-brand-500/5 rounded-md border px-3 py-3">
            <div className="text-fg-muted text-xs">{t('shell.user.devicesPairCode')}</div>
            <div className="mt-1 flex items-center gap-3">
              <span className="font-mono text-2xl font-semibold tracking-[0.25em]" data-testid="pairing-code">
                {pairing.code}
              </span>
              <Button variant="ghost" size="sm" onClick={copyCode}>
                {copied ? <Check className="text-success h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('common:Copied') : t('common:Copy')}
              </Button>
            </div>
            <div className="text-fg-muted mt-1 text-xs">
              {t('shell.user.devicesPairExpires', { time: new Date(pairing.expiresUtc).toLocaleTimeString() })} · {t('shell.user.devicesPairHint')}
            </div>
          </div>
        ) : null}
        {createCode.isError ? <Alert tone="danger">{String((createCode.error as Error)?.message ?? createCode.error)}</Alert> : null}
        {downloadAgent.isError ? <Alert tone="danger">{String((downloadAgent.error as Error)?.message ?? downloadAgent.error)}</Alert> : null}
        {isSys && pkg.data && !agentAvailable ? <Alert tone="warning">{t('shell.user.devicesAgentNoPackage')}</Alert> : null}

        {devices.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : devices.isError ? (
          <Alert tone="danger">{String((devices.error as Error)?.message ?? devices.error)}</Alert>
        ) : !devices.data?.length ? (
          <div className="text-fg-muted py-6 text-center text-sm">{t('shell.user.devicesEmpty')}</div>
        ) : (
          <ul className="divide-border border-border divide-y rounded-md border">
            {devices.data.map((d) => (
              <DeviceRow key={d.deviceId} device={d} isLocal={local.data?.deviceId?.toLowerCase() === d.deviceId.toLowerCase()} isSys={isSys} />
            ))}
          </ul>
        )}

        {/* Yazdırma kuyruğu/geçmişi: çevrimdışı cihaza giden iş burada bekler. */}
        <PrintJobsPanel open={open} isSys={isSys} />
      </div>
    </Dialog>
  );
}

function DeviceRow({ device, isLocal, isSys }: { device: DeviceDto; isLocal: boolean; isSys: boolean }) {
  const { t } = useTranslation();
  const printers = device.printers ?? [];
  // Sistem rolü yazıcıya tıklayıp takma ad / varsayılan / ham yazıcı verir.
  const [editingPrinter, setEditingPrinter] = useState<DevicePrinterDto | null>(null);
  return (
    <li className="flex flex-col gap-1 px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span
          className={cn('h-2.5 w-2.5 shrink-0 rounded-full', device.online ? 'bg-success' : 'bg-fg-subtle')}
          title={device.online ? t('shell.user.devicesOnline') : t('shell.user.devicesOffline')}
          aria-label={device.online ? t('shell.user.devicesOnline') : t('shell.user.devicesOffline')}
        />
        <span className="font-medium">{device.deviceCode}</span>
        {isLocal ? <span className="bg-brand-500/10 text-brand-600 rounded-full px-2 py-0.5 text-[11px]">{t('shell.user.devicesLocal')}</span> : null}
        <span className="text-fg-muted min-w-0 truncate">
          {[device.machineName, device.windowsUser].filter(Boolean).join(' · ')}
        </span>
        <span className="text-fg-subtle ml-auto shrink-0 text-xs">
          {device.agentVersion ? `v${device.agentVersion} · ` : ''}
          {device.lastSeenUtc ? `${t('shell.user.devicesLastSeen')}: ${new Date(device.lastSeenUtc).toLocaleString()}` : ''}
        </span>
      </div>
      <div className="text-fg-muted flex flex-wrap items-center gap-1 pl-4 text-xs">
        <Printer className="h-3.5 w-3.5" />
        {printers.length === 0
          ? t('shell.user.devicesPrinters', { count: 0 })
          : printers.map((p) =>
              isSys ? (
                <button
                  key={p.devicePrinterId}
                  type="button"
                  className={cn('bg-bg-muted hover:bg-bg-subtle rounded px-1.5 py-0.5', p.isDefault && 'font-medium')}
                  title={`${p.printerName} · ${t('shell.user.devicesPrinterEdit')}`}
                  onClick={() => setEditingPrinter(p)}
                >
                  {p.printerAlias ? `${p.printerAlias} (${p.printerName})` : p.printerName}
                  {p.isRawPrinter ? ' · RAW' : ''}
                </button>
              ) : (
                <span key={p.devicePrinterId} className={cn('bg-bg-muted rounded px-1.5 py-0.5', p.isDefault && 'font-medium')} title={p.printerName}>
                  {p.printerAlias ? `${p.printerAlias} (${p.printerName})` : p.printerName}
                </span>
              ),
            )}
      </div>
      {editingPrinter ? <PrinterEditDialog deviceCode={device.deviceCode} printer={editingPrinter} onClose={() => setEditingPrinter(null)} /> : null}
      {/* COM portlar (tartı): yalnız sistem rolü düzenler; profil ajana merkezden iner. */}
      {isSys ? <ComPortsEditor device={device} /> : null}
    </li>
  );
}
